import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, History } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const formatNumber = (n) =>
  typeof n === "number" ? n.toLocaleString("es-MX") : n ?? "—";

export default function UploadsHistory() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/uploads", { params: { limit: 200 } });
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div>
        <h1
          className="text-2xl font-semibold tracking-tight"
          data-testid="uploads-page-title"
        >
          {t("uploads.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("uploads.subtitle")}</p>
      </div>

      <Card data-testid="uploads-history-card">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("uploads.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="uploads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("uploads.filename")}</TableHead>
                    <TableHead>{t("uploads.report")}</TableHead>
                    <TableHead>{t("uploads.uploader")}</TableHead>
                    <TableHead>{t("uploads.uploadedAt")}</TableHead>
                    <TableHead className="text-right">{t("uploads.rowsTotal")}</TableHead>
                    <TableHead className="text-right">{t("uploads.rowsInserted")}</TableHead>
                    <TableHead className="text-right">{t("uploads.rowsDuplicates")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((u) => (
                    <TableRow key={u.id} data-testid={`upload-row-${u.id}`}>
                      <TableCell className="max-w-[16rem] truncate font-mono text-xs">
                        {u.filename}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {u.source_report}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {u.uploaded_by_email || u.uploaded_by}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {u.uploaded_at?.slice(0, 19).replace("T", " ")}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums">
                        {formatNumber(u.row_count_total)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums text-emerald-500">
                        {formatNumber(u.row_count_inserted)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums text-amber-500">
                        {formatNumber(u.row_count_duplicates)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
